/**
 * SourceAttribution — small pill that credits the origin of a seeded listing.
 *
 * Rendered on every card/detail page for `is_external` properties.
 * Doubles as a supply-side acquisition hook: when the listing is
 * un-claimed, hovering flips the pill to "Claim this listing →"
 * pointing at the existing claim flow.
 *
 * Props:
 *   sourceUrl: string          — e.g. https://sherryfitz.ie/property/12345
 *   claimed:   boolean         — whether an agent has claimed the profile
 *   variant:   'card' | 'detail' — sizing preset
 *   onClaim?:  () => void      — invoked when user clicks the claim CTA
 *                                (only wired in `detail` variant)
 */
import React from 'react';
import { ExternalLink, ArrowUpRight } from 'lucide-react';

// Human-friendly display for the source domain. Strips leading www.,
// caps to 22 chars so it doesn't blow out the card layout.
const prettyHost = (url) => {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '');
    return host.length > 22 ? host.slice(0, 19) + '…' : host;
  } catch (_) {
    return 'source';
  }
};

const SourceAttribution = ({ sourceUrl, claimed = false, variant = 'card', onClaim }) => {
  if (!sourceUrl) return null;
  const host = prettyHost(sourceUrl);

  if (variant === 'detail') {
    // Detail-page treatment: full-width panel that morphs based on
    // claim state. Claimed listings get a neutral attribution; un-
    // claimed ones become an active acquisition prompt.
    return (
      <div
        className={`rounded-lg border px-3 py-2.5 text-[12px] flex items-center gap-3 flex-wrap ${
          claimed
            ? 'bg-gray-50 border-gray-200 text-gray-600'
            : 'bg-purple-50/60 border-purple-200 text-[#5b35a8]'
        }`}
        data-testid="source-attribution-detail"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">
            Sourced from
          </div>
          <a
            href={sourceUrl}
            target="_blank"
            rel="noreferrer nofollow"
            className="font-bold hover:underline inline-flex items-center gap-1 max-w-full truncate"
            data-testid="source-attribution-link"
          >
            {host}
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>
        </div>
        {!claimed && onClaim && (
          <button
            type="button"
            onClick={onClaim}
            className="text-[11px] font-bold uppercase tracking-wider bg-[#7042cc] text-white px-3 py-1.5 rounded-md hover:bg-[#5b35a8] inline-flex items-center gap-1"
            data-testid="source-attribution-claim-cta"
          >
            Is this yours? Claim it
            <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  // Card treatment: tiny footer pill so cards stay clean. Clickable, no
  // claim CTA (that lives on the detail page where the modal opens). Rendered
  // as a <button> to avoid nesting <a> inside the outer PropertyCard <Link>
  // (React 18 hydration error). We navigate to the source URL programmatically.
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window !== 'undefined') {
          window.open(sourceUrl, '_blank', 'noopener,noreferrer,nofollow');
        }
      }}
      className="inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#5b35a8] transition"
      data-testid="source-attribution-card"
      title={sourceUrl}
    >
      <ExternalLink className="h-2.5 w-2.5" />
      Sourced from {host}
    </button>
  );
};

export default SourceAttribution;
